import React from 'react';
import { css } from '@emotion/react';
import { i18n_get_comfy_lean_back_and_learn_about_ancient_wisdom_of_yoga_with_our_docs_and_films } from '@yi/i18n/phrases/i18n_get_comfy_lean_back_and_learn_about_ancient_wisdom_of_yoga_with_our_docs_and_films';
import { i18n_get_inspired_through_docs_films } from '@yi/i18n/phrases/i18n_get_inspired_through_docs_films';

import { getOptimizedImgSrc } from '~/src/common/image';
import { useLocale } from '~/src/components/LocaleProvider';
import { EmbeddedPlayer } from '~/src/components/MediaPlayer/EmbeddedPlayer';
import { colors, paddingVariables, typography } from '~/src/styles/design-system';
import { resetStyles } from '~/src/styles/reset-styles-mixins';
import { breakpoints } from '~/src/styles/styled-mixins';

import { DocsAndFilmsCarousel } from './DocsAndFilmsCarousel';
import { ReactComponent as Mandala } from './mandala.svg';
import { useIsSSR } from '../../hooks/useIsSSR';
import { Poster } from '../../MediaPlayer/Poster';
import { commonStyles } from '../common-styles';

const seriesTrailerMediaIdEN = '6331202494112';
const seriesTrailerMediaIdES = '6331202398112';

const seriesTrailerPosterEN =
  'https://images.ctfassets.net/p0sybd6jir6r/5Ey7Wsev3aZG54dUQl7X78/f6eb1cbfa57f8e9fe35ca580775d95c9/EN-TV-background_wo_borders.png';

const seriesTrailerPosterES =
  'https://images.ctfassets.net/p0sybd6jir6r/1s9HywwlsQvNPcKzgHTysy/e44cbbafccb339d65245e55ca3b5cd54/ES-TV-background_wo_borders.png';

const styles = {
  tvContainer: css({
    paddingLeft: 'var(--Mobile-Horizontal-Padding)',
    paddingRight: 'var(--Mobile-Horizontal-Padding)',
    maxWidth: '580px',
    [breakpoints.minTablet]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    [breakpoints.minDesktop]: {
      maxWidth: '720px',
    },
    position: 'relative',
    zIndex: '10',
    width: '100%',
    gridArea: 'tvContainer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 'auto',
  }),
  mandala: css({
    width: '90%',
    zIndex: '5',
    margin: 'auto',
    opacity: 0.5,
    [breakpoints.minTablet]: {
      width: '81%',
    },
    [breakpoints.minDesktop]: {
      width: '82%',
    },
  }),
  tvImg: css({
    zIndex: '10',
    position: 'relative',
    width: '100%',
  }),
  container: css({
    display: 'grid',
    overflowX: 'hidden',
    margin: 'auto',
    gridTemplateAreas: `
        '.'
        'title'
        '.'
        'details'
        '.'
        'tvContainer'
        '.'
        'carousel'
        '.'
      `,
    gridTemplateRows: `
        var(--Section-Padding)
        auto
        var(--Heading-Padding)
        auto
        ${paddingVariables.p56}
        auto
        ${paddingVariables.p56}
        auto
        var(--Section-Padding)
    `,
    [breakpoints.minDesktop]: css({
      gridTemplateAreas: `
        '. . . '
        '. title .'
        '. . .'
        '. details .'
        '. . .'
        '. tvContainer .'
        '. . .'
        '. carousel .'
        '. . .'
      `,
      gridTemplateRows: `
        var(--Section-Padding)
        auto
        var(--Heading-Padding)
        auto
        ${paddingVariables.p120}
        auto
        ${paddingVariables.p120}
        auto
        var(--Section-Padding)
    `,
      gridTemplateColumns: `1fr var(--Max-Desktop-Width) 1fr`,
    }),
  }),
  heading: css(commonStyles.contentPadding, typography.heading1, {
    zIndex: 10,
    gridArea: 'title',
    color: 'var(--Dark-Text, #27292D)',
  }),
  details: css(resetStyles.heading, commonStyles.contentPadding, typography.bodyText, {
    zIndex: 10,
    color: 'var(--Grey-Text, #585858)',
    gridArea: 'details',
    margin: 0,
    [breakpoints.minTablet]: [
      typography.subHeading1,
      {
        color: 'var(--Grey-Text, #585858)',
      },
    ],
  }),
  carousel: css(commonStyles.contentPadding, commonStyles.carouselContainerStyles, {
    gridArea: 'carousel',
  }),
  background: css({
    zIndex: 1,
    gridArea: 'background',
    borderRadius: 'var(--Border-Radius)',
    background: 'var(--Primary-Background, rgba(255, 243, 226, 0.50))',
    gridColumnStart: '1',
    gridRowStart: '1',
    gridRowEnd: '-1',
  }),
  playerContainer: css({
    borderRadius: '20px',
    position: 'absolute',
    aspectRatio: '16/9',
    width: '100%',
    maxWidth: 'calc(100% - 2 * var(--Mobile-Horizontal-Padding))',
    background: `${colors.DarkText}ad`,
    overflow: 'hidden',
    zIndex: 20,
    '& .vjs-big-play-button': {
      width: '44px !important',
      height: '44px !important',
      color: 'var(--Dark-Text) !important',
      fontSize: '36px !important',
      lineHeight: '46px !important',
    },
  }),
};

export const DocsAndFilms: React.FC = () => {
  const { locale } = useLocale();
  const isSSR = useIsSSR();

  return (
    <section css={styles.container}>
      <div css={styles.background} />
      <h1 css={styles.heading}>{i18n_get_inspired_through_docs_films(locale)}</h1>
      <h2 css={styles.details}>
        {i18n_get_comfy_lean_back_and_learn_about_ancient_wisdom_of_yoga_with_our_docs_and_films(
          locale,
        )}
      </h2>
      <div css={styles.tvContainer}>
        <div css={styles.playerContainer}>
          {!isSSR && (
            <EmbeddedPlayer
              mediaId={locale === 'es' ? seriesTrailerMediaIdES : seriesTrailerMediaIdEN}
            >
              <Poster
                imgSrc={getOptimizedImgSrc({
                  src: locale === 'es' ? seriesTrailerPosterES : seriesTrailerPosterEN,
                  width: 500,
                  quality: 50,
                  format: 'webp',
                })}
              />
            </EmbeddedPlayer>
          )}
        </div>
        <Mandala css={styles.mandala} />
      </div>
      <DocsAndFilmsCarousel css={styles.carousel} />
    </section>
  );
};
